import { useEffect, useRef, useState } from 'react'
import {
  BadgeCheck,
  FileText,
  Landmark,
  Phone,
  Upload,
} from 'lucide-react'
import Swal from 'sweetalert2'
import Lang from '../../../config/lang'
import { baseUrl } from '../../../config/data'
import { submitMyPreOnBoarding, uploadMyPreOnBoardFile } from '../api'
import ApplicationMetaSummary from './ApplicationMetaSummary'
import ApplicationNotice from './ApplicationNotice'
import ApplicationProgressTimeline from './ApplicationProgressTimeline'

const DEFAULT_EXPIRY_DATE = '2026-04-12'
const DEFAULT_EXPIRY_COUNTDOWN = '120:00:00'
const PHONE_COUNTRY_CODE = '62'
const MAX_UPLOAD_SIZE_BYTES = 2 * 1024 * 1024
const ALLOWED_UPLOAD_EXTENSIONS = ['jpg', 'jpeg', 'png', 'pdf']
const NPWP_FORMAT_DELAY_MS = 1500

const PERSON_NAME_PATTERN = /^[A-Za-z\s.'-]+$/
const RELATIONSHIP_PATTERN = /^[A-Za-z\s.&'/-]+$/
const BANK_NAME_PATTERN = /^[A-Za-z0-9\s.&'/-]+$/

const INITIAL_FORM_VALUES = {
  ktpNumber: '',
  kkNumber: '',
  npwpNumber: '',
  bankName: 'BCA',
  bankAccountNumber: '',
  bankAccountHolderName: '',
  emergencyContactName: '',
  emergencyContactPhone: `+${PHONE_COUNTRY_CODE}`,
  emergencyContactRelationship: '',
}

const INITIAL_UPLOADED_FILES = {
  ktp: null,
  kk: null,
  npwp: null,
}

const REQUIRED_TOUCHED_FIELDS = {
  ktpNumber: true,
  kkNumber: true,
  bankName: true,
  bankAccountNumber: true,
  bankAccountHolderName: true,
  emergencyContactName: true,
  emergencyContactPhone: true,
  emergencyContactRelationship: true,
  ktpFile: true,
  kkFile: true,
  declaration: true,
}

const inputBaseClassName =
  'h-[38px] w-full rounded-[10px] bg-white px-4 text-[14px] font-normal leading-5 text-[#0F1729] shadow-[inset_0px_1px_2px_rgba(16,24,40,0.04)] placeholder:font-normal placeholder:text-[#6B7280] transition focus:outline-none'

const normalizeSpaces = (value) =>
  String(value || '')
    .replace(/\s+/g, ' ')
    .trim()
const digitsOnly = (value) => String(value || '').replace(/\D/g, '')
const getKtpDigits = (value) => digitsOnly(value).slice(0, 16)
const getKkDigits = (value) => digitsOnly(value).slice(0, 16)
const getNpwpDigits = (value) => digitsOnly(value).slice(0, 15)
const getAccountDigits = (value) => digitsOnly(value).slice(0, 20)
const getPhoneDigits = (value) => {
  const rawDigits = digitsOnly(value)

  if (!rawDigits) return PHONE_COUNTRY_CODE

  let normalizedDigits = rawDigits

  if (normalizedDigits.startsWith(`${PHONE_COUNTRY_CODE}0`)) {
    normalizedDigits = `${PHONE_COUNTRY_CODE}${normalizedDigits.slice(3)}`
  } else if (normalizedDigits.startsWith('0')) {
    normalizedDigits = `${PHONE_COUNTRY_CODE}${normalizedDigits.slice(1)}`
  } else if (!normalizedDigits.startsWith(PHONE_COUNTRY_CODE)) {
    normalizedDigits = `${PHONE_COUNTRY_CODE}${normalizedDigits}`
  }

  return normalizedDigits.slice(0, 15)
}
const formatPhoneValue = (value) => `+${getPhoneDigits(value)}`
const getFileExtension = (name) =>
  String(name || '')
    .split('.')
    .pop()
    ?.toLowerCase() || ''

const isImageFile = (file) => {
  if (!file) return false

  return (
    String(file?.type || '').startsWith('image/') ||
    /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(String(file?.name || ''))
  )
}

const formatNpwpNumber = (digits) => {
  if (digits.length !== 15) return digits

  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(
    5,
    8,
  )}.${digits.slice(8, 9)}-${digits.slice(9, 12)}.${digits.slice(12, 15)}`
}

const formatExpiryDateDisplay = (value, lang) => {
  if (!value) return ''

  const normalizedValue = String(value).includes('T')
    ? value
    : `${value}T00:00:00`
  const date = new Date(normalizedValue)

  if (Number.isNaN(date.getTime())) return value

  return new Intl.DateTimeFormat(lang === 'id' ? 'id-ID' : 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
}

const isValidPhoneNumber = (digits) =>
  digits.length >= 10 &&
  digits.length <= 15 &&
  digits.startsWith(PHONE_COUNTRY_CODE)

const isAcceptedUploadFile = (file) =>
  ALLOWED_UPLOAD_EXTENSIONS.includes(getFileExtension(file?.name))

const createUploadedFileState = (file) => {
  if (!file) return null

  const imageFile = isImageFile(file)
  const pdfFile =
    String(file?.type || '')
      .toLowerCase()
      .includes('pdf') || /\.pdf$/i.test(String(file?.name || ''))
  const previewable = imageFile || pdfFile

  return {
    rawFile: file,
    name: file.name,
    type: file.type,
    size: file.size,
    previewUrl: previewable ? URL.createObjectURL(file) : '',
    isImage: imageFile,
    isPdf: pdfFile,
    serverPath: '',
    shouldRevokePreview: previewable,
  }
}

const createExistingUploadedFileState = (filePath) => {
  if (!filePath) return null

  const normalizedPath = String(filePath).trim()
  const fileName = normalizedPath.split('/').pop() || normalizedPath
  const imageFile = /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(normalizedPath)
  const pdfFile = /\.pdf$/i.test(normalizedPath)
  const resolvedUrl = /^https?:\/\//i.test(normalizedPath)
    ? normalizedPath
    : `${baseUrl}/${normalizedPath.replace(/^\/+/, '')}`

  return {
    rawFile: null,
    name: fileName,
    type: '',
    size: 0,
    previewUrl: imageFile || pdfFile ? resolvedUrl : '',
    isImage: imageFile,
    isPdf: pdfFile,
    serverPath: normalizedPath,
    shouldRevokePreview: false,
  }
}

const getFieldErrorKey = (field, values) => {
  switch (field) {
    case 'ktpNumber': {
      const digits = getKtpDigits(values.ktpNumber)
      if (!digits) return 'pre_onboarding_ktp_required'
      if (digits.length !== 16) return 'pre_onboarding_ktp_invalid'
      return ''
    }
    case 'kkNumber': {
      const digits = getKkDigits(values.kkNumber)
      if (!digits) return 'pre_onboarding_kk_required'
      if (digits.length !== 16) return 'pre_onboarding_kk_invalid'
      return ''
    }
    case 'npwpNumber': {
      const digits = getNpwpDigits(values.npwpNumber)
      if (!digits) return ''
      if (digits.length !== 15) return 'pre_onboarding_npwp_invalid'
      return ''
    }
    case 'bankName': {
      const value = normalizeSpaces(values.bankName)
      if (!value) return 'pre_onboarding_bank_name_required'
      if (value.length < 2 || !BANK_NAME_PATTERN.test(value))
        return 'pre_onboarding_bank_name_invalid'
      return ''
    }
    case 'bankAccountNumber': {
      const digits = getAccountDigits(values.bankAccountNumber)
      if (!digits) return 'pre_onboarding_account_number_required'
      if (digits.length < 8 || digits.length > 20)
        return 'pre_onboarding_account_number_invalid'
      return ''
    }
    case 'bankAccountHolderName': {
      const value = normalizeSpaces(values.bankAccountHolderName)
      if (!value) return 'pre_onboarding_account_holder_name_required'
      if (value.length < 3 || !PERSON_NAME_PATTERN.test(value))
        return 'pre_onboarding_account_holder_name_invalid'
      return ''
    }
    case 'emergencyContactName': {
      const value = normalizeSpaces(values.emergencyContactName)
      if (!value) return 'pre_onboarding_emergency_contact_name_required'
      if (value.length < 3 || !PERSON_NAME_PATTERN.test(value))
        return 'pre_onboarding_emergency_contact_name_invalid'
      return ''
    }
    case 'emergencyContactPhone': {
      const digits = getPhoneDigits(values.emergencyContactPhone)
      if (!digits || digits === PHONE_COUNTRY_CODE)
        return 'pre_onboarding_emergency_contact_phone_required'
      if (!isValidPhoneNumber(digits))
        return 'pre_onboarding_emergency_contact_phone_invalid'
      return ''
    }
    case 'emergencyContactRelationship': {
      const value = normalizeSpaces(values.emergencyContactRelationship)
      if (!value) return 'pre_onboarding_relationship_required'
      if (value.length < 2 || !RELATIONSHIP_PATTERN.test(value))
        return 'pre_onboarding_relationship_invalid'
      return ''
    }
    default:
      return ''
  }
}

const getUploadErrorKey = (file, { required = false } = {}) => {
  if (!file) return required ? 'pre_onboarding_file_required' : ''
  if (!isAcceptedUploadFile(file)) return 'pre_onboarding_file_invalid'
  if (Number(file?.size || 0) > MAX_UPLOAD_SIZE_BYTES)
    return 'pre_onboarding_file_too_large'
  return ''
}

const buildValidationErrors = (values, files, hasDeclared) => {
  const nextErrors = {}

  ;[
    'ktpNumber',
    'kkNumber',
    'npwpNumber',
    'bankName',
    'bankAccountNumber',
    'bankAccountHolderName',
    'emergencyContactName',
    'emergencyContactPhone',
    'emergencyContactRelationship',
  ].forEach((field) => {
    const errorKey = getFieldErrorKey(field, values)

    if (errorKey) {
      nextErrors[field] = errorKey
    }
  })

  const ktpFileError = getUploadErrorKey(files.ktp, { required: true })
  const kkFileError = getUploadErrorKey(files.kk, { required: true })
  const npwpFileError = getUploadErrorKey(files.npwp)

  if (ktpFileError) nextErrors.ktpFile = ktpFileError
  if (kkFileError) nextErrors.kkFile = kkFileError
  if (npwpFileError) nextErrors.npwpFile = npwpFileError
  if (!hasDeclared) {
    nextErrors.declaration = 'pre_onboarding_declaration_required'
  }

  return nextErrors
}

const StatusBadge = ({
  label,
  tone = 'required',
  variant = 'summary',
  className = '',
}) => {
  const toneClassName =
    tone === 'optional'
      ? variant === 'section'
        ? 'border-[rgba(107,114,128,0.30)] bg-white text-[#6B7280]'
        : 'border-[rgba(107,114,128,0.30)] bg-[rgba(107,114,128,0.08)] text-[#6B7280]'
      : variant === 'section'
        ? 'border-[rgba(232,23,44,0.30)] bg-white text-[#E8172C]'
        : 'border-[rgba(232,23,44,0.30)] bg-[rgba(232,23,44,0.10)] text-[#E8172C]'
  const sizeClassName =
    variant === 'section'
      ? 'px-[6px] py-0 text-[10px] font-bold leading-[15px]'
      : 'px-[10px] py-[2px] text-[12px] font-bold leading-4'

  return (
    <span
      className={`inline-flex w-fit items-center rounded-full border ${sizeClassName} ${toneClassName} ${className}`}
    >
      {label}
    </span>
  )
}

const TextField = ({
  lang,
  label,
  placeholder,
  value,
  onChange,
  onBlur,
  error = '',
  readOnly = false,
  disabled = false,
  inputMode = 'text',
  maxLength,
  className = '',
}) => (
  <label className={`block ${className}`}>
    <span className='mb-2 block text-[12px] font-normal leading-[16px] text-[#0F1729]'>
      {label}
    </span>

    <input
      type='text'
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      readOnly={readOnly}
      disabled={disabled}
      autoComplete='off'
      className={`${inputBaseClassName} ${
        readOnly || disabled
          ? 'border border-[#E5E7EB] bg-[#F3F4F6] text-[#6B7280]'
          : error
            ? 'border border-[#EA2427] ring-1 ring-[#FECACA]'
            : 'border border-[#E5E7EB] focus:border-[#48494B] focus:ring-0'
      }`}
    />

    {error ? (
      <p className='mt-1 text-[10px] leading-[16px] text-[#EA2427]'>
        {Lang(lang, error)}
      </p>
    ) : null}
  </label>
)

const UploadArea = ({
  lang,
  prompt,
  file,
  error,
  onFileChange,
  isUploading = false,
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const dragDepthRef = useRef(0)

  const handleSelectedFile = (selectedFile) => {
    if (!selectedFile || disabled) return

    onFileChange(selectedFile)
    dragDepthRef.current = 0
    setIsDragging(false)
  }

  const handleDragOver = (event) => {
    if (disabled) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'copy'
    setIsDragging(true)
  }

  const handleDragEnter = (event) => {
    if (disabled) return
    event.preventDefault()
    dragDepthRef.current += 1
    setIsDragging(true)
  }

  const handleDragLeave = (event) => {
    if (disabled) return
    event.preventDefault()
    dragDepthRef.current = Math.max(dragDepthRef.current - 1, 0)

    if (dragDepthRef.current === 0) {
      setIsDragging(false)
    }
  }

  const handleDrop = (event) => {
    if (disabled) return
    event.preventDefault()
    handleSelectedFile(event.dataTransfer.files?.[0])
  }

  const containerClassName = isDragging
    ? 'border-[#EA2427] bg-[#FFF5F5]'
    : error
      ? 'border-[#EA2427] bg-[#FFF8F8]'
      : 'border-[#D0D5DD] bg-white'

  return (
    <div>
      <div
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`rounded-[12px] border border-dashed px-4 py-6 text-center transition sm:px-6 sm:py-8 ${containerClassName}`}
      >
        {file?.isImage && file?.previewUrl ? (
          <div className='mx-auto mb-4 h-[140px] w-full max-w-[240px] overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-[#F9FAFB]'>
            <img
              src={file.previewUrl}
              alt={file.name}
              draggable={false}
              onDragStart={(event) => event.preventDefault()}
              className='h-full w-full object-cover'
            />
          </div>
        ) : file?.isPdf && file?.previewUrl ? (
          <div className='mx-auto mb-4 h-[180px] w-full max-w-[320px] overflow-hidden rounded-[12px] border border-[#E5E7EB] bg-white'>
            <iframe
              title={file.name}
              src={file.previewUrl}
              draggable={false}
              className='h-full w-full'
            />
          </div>
        ) : (
          <div className='mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-[#F4F4F5] text-[#0F1729]'>
            <Upload className='h-5 w-5' strokeWidth={1.8} />
          </div>
        )}

        <p className='mt-4 text-[13px] font-medium leading-[21px] text-[#111827]'>
          {isUploading
            ? Lang(lang, 'uploading')
            : isDragging
              ? Lang(lang, 'upload_prompt_drop_or_click')
              : prompt}
        </p>

        <p className='mt-2 text-[10px] leading-[16px] text-[#6B7280]'>
          {Lang(lang, 'upload_support_formats')}
        </p>

        {file?.name ? (
          <p className='mt-3 break-words text-[12px] font-medium leading-4 text-[#0F1729]'>
            {file.name}
          </p>
        ) : null}

        <label
          className={`mt-6 inline-flex items-center justify-center rounded-full px-6 py-3 text-[14px] font-semibold leading-5 text-white transition ${
            disabled || isUploading
              ? 'cursor-not-allowed bg-[#FCA5A5]'
              : 'cursor-pointer bg-[#EA2427] hover:bg-[#D91F22]'
          }`}
        >
          {isUploading ? Lang(lang, 'uploading') : Lang(lang, 'choose_file')}
          <input
            type='file'
            accept='.jpg,.jpeg,.png,.pdf'
            className='sr-only'
            disabled={disabled || isUploading}
            onChange={(event) => handleSelectedFile(event.target.files?.[0])}
          />
        </label>
      </div>

      {error ? (
        <p className='mt-2 text-xs leading-4 text-[#EA2427]'>
          {Lang(lang, error)}
        </p>
      ) : null}
    </div>
  )
}

const ChecklistSectionCard = ({
  icon,
  title,
  badgeLabel,
  badgeTone = 'required',
  description,
  children,
}) => (
  <div className='rounded-[20px] border border-[#E5E7EB] bg-white p-5 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-6'>
    <div className='flex flex-col gap-4 sm:flex-row sm:items-start'>
      <div className='flex h-[40px] w-[40px] flex-shrink-0 items-center justify-center rounded-[12px] bg-[#F3F4F6] text-[#6B7280]'>
        {icon}
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex flex-col gap-2 sm:flex-row sm:items-center'>
          <h4 className='text-[14px] font-bold leading-[20px] text-[#0F1729]'>
            {title}
          </h4>
          <StatusBadge label={badgeLabel} tone={badgeTone} variant='section' />
        </div>

        <p className='mt-1 text-[12px] leading-[16px] font-normal text-[#6B7280]'>
          {description}
        </p>
      </div>
    </div>

    <div className='mt-6 space-y-5'>{children}</div>
  </div>
)

const PreOnBoardingCard = ({ application, lang, userToken, onRefresh }) => {
  const [formValues, setFormValues] = useState(INITIAL_FORM_VALUES)
  const [uploadedFiles, setUploadedFiles] = useState(INITIAL_UPLOADED_FILES)
  const [touchedFields, setTouchedFields] = useState({})
  const [errors, setErrors] = useState({})
  const [submitAttempted, setSubmitAttempted] = useState(false)
  const [hasDeclared, setHasDeclared] = useState(false)
  const [isSubmittingForm, setIsSubmittingForm] = useState(false)
  const [uploadingFields, setUploadingFields] = useState({})
  const [hasSubmittedPreOnboarding, setHasSubmittedPreOnboarding] = useState(
    String(application?.jobOffer?.status || '').trim() ===
      'PreOnBoardingSubmitted',
  )

  const uploadedFilesRef = useRef(uploadedFiles)
  const npwpFormatTimeoutRef = useRef(null)

  const progressSteps =
    application?.progressSteps || application?.overviewMeta?.progressSteps || []
  const companyLabel =
    application?.company != null && String(application.company).trim() !== ''
      ? application.company
      : '-'
  const locationLabel =
    application?.jobOffer?.location || application?.location || '-'
  const preOnboarding = application?.preOnboarding || {}
  const currentJobOffer = application?.jobOffer || {}
  const isSubmitted =
    hasSubmittedPreOnboarding ||
    String(currentJobOffer?.status || '').trim() === 'PreOnBoardingSubmitted'
  const isExpired =
    String(currentJobOffer?.status || '').trim() === 'PreOnBoardingExpired'
  const isFormLocked = isSubmitted || isExpired
  const countdownSourceValue =
    preOnboarding?.countdown || preOnboarding?.remainingTime || ''
  const countdownTargetValue =
    preOnboarding?.expiresAt || preOnboarding?.expiryDate || ''
  const expiryDateLabel =
    formatExpiryDateDisplay(countdownTargetValue, lang) ||
    formatExpiryDateDisplay(DEFAULT_EXPIRY_DATE, lang)
  const declarationStatements = [
    Lang(lang, 'pre_onboarding_declaration_item_1'),
    Lang(lang, 'pre_onboarding_declaration_item_2'),
    Lang(lang, 'pre_onboarding_declaration_item_3'),
  ]

  useEffect(() => {
    setHasSubmittedPreOnboarding(
      String(currentJobOffer?.status || '').trim() === 'PreOnBoardingSubmitted',
    )
  }, [currentJobOffer?.candidateJobOfferID, currentJobOffer?.status])

  const updateErrorState = (field, errorKey) => {
    setErrors((previousErrors) => {
      const nextErrors = { ...previousErrors }

      if (errorKey) {
        nextErrors[field] = errorKey
      } else {
        delete nextErrors[field]
      }

      return nextErrors
    })
  }

  const markFieldAsTouched = (field) => {
    setTouchedFields((previousFields) =>
      previousFields[field]
        ? previousFields
        : { ...previousFields, [field]: true },
    )
  }

  const getVisibleError = (field) =>
    submitAttempted || touchedFields[field] || field === 'npwpNumber'
      ? errors[field] || ''
      : ''

  const sanitizeFieldValue = (field, value) => {
    switch (field) {
      case 'ktpNumber':
        return getKtpDigits(value)
      case 'kkNumber':
        return getKkDigits(value)
      case 'npwpNumber':
        return getNpwpDigits(value)
      case 'bankAccountNumber':
        return getAccountDigits(value)
      case 'emergencyContactPhone':
        return formatPhoneValue(value)
      default:
        return value
    }
  }

  const requiredSections = [
    {
      key: 'ktp',
      completed: Boolean(
        !getFieldErrorKey('ktpNumber', formValues) &&
        !getUploadErrorKey(uploadedFiles.ktp, { required: true }),
      ),
    },
    {
      key: 'kk',
      completed: Boolean(
        !getFieldErrorKey('kkNumber', formValues) &&
        !getUploadErrorKey(uploadedFiles.kk, { required: true }),
      ),
    },
    {
      key: 'bank',
      completed: Boolean(
        !getFieldErrorKey('bankName', formValues) &&
        !getFieldErrorKey('bankAccountNumber', formValues) &&
        !getFieldErrorKey('bankAccountHolderName', formValues),
      ),
    },
    {
      key: 'emergency',
      completed: Boolean(
        !getFieldErrorKey('emergencyContactName', formValues) &&
        !getFieldErrorKey('emergencyContactPhone', formValues) &&
        !getFieldErrorKey('emergencyContactRelationship', formValues),
      ),
    },
  ]

  const requiredCompletedCount = requiredSections.filter(
    (section) => section.completed,
  ).length
  const progressWidth = `${
    (requiredCompletedCount / requiredSections.length) * 100
  }%`
  const isUploadingAnyField = Object.values(uploadingFields).some(
    (isUploading) => isUploading === true,
  )
  const isPreOnboardingSubmitDisabled =
    isFormLocked ||
    isSubmittingForm ||
    isUploadingAnyField ||
    Object.keys(buildValidationErrors(formValues, uploadedFiles, hasDeclared))
      .length > 0

  useEffect(() => {
    setFormValues({
      ktpNumber: currentJobOffer?.ktpNumber || '',
      kkNumber: currentJobOffer?.kkNumber || '',
      npwpNumber: currentJobOffer?.npwpNumber || '',
      bankName: currentJobOffer?.bankName || 'BCA',
      bankAccountNumber: currentJobOffer?.bankAccountNumber || '',
      bankAccountHolderName: currentJobOffer?.bankAccountHolderName || '',
      emergencyContactName: currentJobOffer?.emergencyContactName || '',
      emergencyContactPhone:
        currentJobOffer?.emergencyContactPhone || `+${PHONE_COUNTRY_CODE}`,
      emergencyContactRelationship:
        currentJobOffer?.emergencyContactRelationship || '',
    })

    setUploadedFiles((previousFiles) => {
      Object.values(previousFiles).forEach((file) => {
        if (file?.previewUrl && file?.shouldRevokePreview) {
          URL.revokeObjectURL(file.previewUrl)
        }
      })

      return {
        ktp: createExistingUploadedFileState(currentJobOffer?.ktpFileURL),
        kk: createExistingUploadedFileState(currentJobOffer?.kkFileURL),
        npwp: createExistingUploadedFileState(currentJobOffer?.npwpFileURL),
      }
    })

    setTouchedFields({})
    setErrors({})
    setSubmitAttempted(false)
    setHasDeclared(currentJobOffer?.isDeclarationAccepted === true)
    setUploadingFields({})
    setIsSubmittingForm(false)
  }, [
    currentJobOffer?.bankAccountHolderName,
    currentJobOffer?.bankAccountNumber,
    currentJobOffer?.bankName,
    currentJobOffer?.emergencyContactName,
    currentJobOffer?.emergencyContactPhone,
    currentJobOffer?.emergencyContactRelationship,
    currentJobOffer?.isDeclarationAccepted,
    currentJobOffer?.ktpFileURL,
    currentJobOffer?.ktpNumber,
    currentJobOffer?.kkFileURL,
    currentJobOffer?.kkNumber,
    currentJobOffer?.npwpFileURL,
    currentJobOffer?.npwpNumber,
  ])

  useEffect(() => {
    uploadedFilesRef.current = uploadedFiles
  }, [uploadedFiles])

  useEffect(() => {
    return () => {
      if (npwpFormatTimeoutRef.current) {
        clearTimeout(npwpFormatTimeoutRef.current)
      }

      Object.values(uploadedFilesRef.current).forEach((file) => {
        if (file?.previewUrl && file?.shouldRevokePreview) {
          URL.revokeObjectURL(file.previewUrl)
        }
      })
    }
  }, [])

  useEffect(() => {
    const npwpDigits = getNpwpDigits(formValues.npwpNumber)

    if (npwpFormatTimeoutRef.current) {
      clearTimeout(npwpFormatTimeoutRef.current)
    }

    if (!npwpDigits) {
      if (submitAttempted || touchedFields.npwpNumber) {
        updateErrorState(
          'npwpNumber',
          getFieldErrorKey('npwpNumber', formValues),
        )
      } else {
        updateErrorState('npwpNumber', '')
      }

      return undefined
    }

    npwpFormatTimeoutRef.current = setTimeout(() => {
      const errorKey =
        npwpDigits.length === 15 ? '' : 'pre_onboarding_npwp_invalid'

      if (!errorKey) {
        const formattedValue = formatNpwpNumber(npwpDigits)

        setFormValues((previousValues) =>
          previousValues.npwpNumber === formattedValue
            ? previousValues
            : {
                ...previousValues,
                npwpNumber: formattedValue,
              },
        )
      }

      updateErrorState('npwpNumber', errorKey)
    }, NPWP_FORMAT_DELAY_MS)

    return () => {
      if (npwpFormatTimeoutRef.current) {
        clearTimeout(npwpFormatTimeoutRef.current)
      }
    }
  }, [formValues.npwpNumber, submitAttempted, touchedFields.npwpNumber])

  const handleInputChange = (field) => (event) => {
    if (isFormLocked) return

    const nextValue = sanitizeFieldValue(field, event.target.value)
    const nextValues = {
      ...formValues,
      [field]: nextValue,
    }

    setFormValues(nextValues)

    if (submitAttempted || touchedFields[field]) {
      updateErrorState(field, getFieldErrorKey(field, nextValues))
    }
  }

  const handleFieldBlur = (field) => () => {
    if (isFormLocked) return

    markFieldAsTouched(field)

    let nextValues = formValues

    if (field === 'npwpNumber') {
      const npwpDigits = getNpwpDigits(formValues.npwpNumber)

      if (npwpDigits.length === 15) {
        nextValues = {
          ...formValues,
          npwpNumber: formatNpwpNumber(npwpDigits),
        }
        setFormValues(nextValues)
      }
    }

    updateErrorState(field, getFieldErrorKey(field, nextValues))
  }

  const handleFileChange =
    (field, errorField, options = {}) =>
    (file) => {
      if (isFormLocked) return

      markFieldAsTouched(errorField)

      if (!file) {
        updateErrorState(errorField, getUploadErrorKey(null, options))
        return
      }

      const uploadErrorKey = getUploadErrorKey(file, options)

      if (uploadErrorKey) {
        updateErrorState(errorField, uploadErrorKey)
        return
      }

      const nextFileState = createUploadedFileState(file)

      setUploadedFiles((previousFiles) => {
        if (
          previousFiles[field]?.previewUrl &&
          previousFiles[field]?.shouldRevokePreview
        ) {
          URL.revokeObjectURL(previousFiles[field].previewUrl)
        }

        return {
          ...previousFiles,
          [field]: nextFileState,
        }
      })

      updateErrorState(errorField, '')
    }

  const handleDeclarationToggle = () => {
    if (isFormLocked) return

    const nextValue = !hasDeclared

    setHasDeclared(nextValue)
    markFieldAsTouched('declaration')
    updateErrorState(
      'declaration',
      nextValue ? '' : 'pre_onboarding_declaration_required',
    )
  }

  const handleSubmit = async () => {
    if (isFormLocked || !currentJobOffer?.candidateJobOfferID) return

    setSubmitAttempted(true)
    setTouchedFields((previousFields) => ({
      ...previousFields,
      ...REQUIRED_TOUCHED_FIELDS,
    }))

    const nextErrors = buildValidationErrors(
      formValues,
      uploadedFiles,
      hasDeclared,
    )
    setErrors(nextErrors)

    if (Object.keys(nextErrors).length > 0) {
      return
    }

    if (!userToken) {
      Swal.fire({
        title: 'Error!',
        text: Lang(lang, 'applications_login_required'),
        icon: 'error',
        timer: 2600,
        showConfirmButton: false,
        customClass: { popup: 'my-custom-popup' },
        heightAuto: false,
        returnFocus: false,
      })
      return
    }

    setIsSubmittingForm(true)

    let ktpFileURL = uploadedFiles.ktp?.serverPath || ''
    let kkFileURL = uploadedFiles.kk?.serverPath || ''
    let npwpFileURL = uploadedFiles.npwp?.serverPath || ''

    if (uploadedFiles.ktp?.rawFile) {
      setUploadingFields((previousFields) => ({
        ...previousFields,
        ktp: true,
      }))

      const uploadResponse = await uploadMyPreOnBoardFile({
        file: uploadedFiles.ktp.rawFile,
        userToken,
        candidateJobOfferID: currentJobOffer.candidateJobOfferID,
        documentType: 'ktp',
      })

      setUploadingFields((previousFields) => ({
        ...previousFields,
        ktp: false,
      }))

      const attachmentPath =
        uploadResponse?.attachmentPath || uploadResponse?.AttachmentPath || ''

      if (typeof uploadResponse === 'string' || !attachmentPath) {
        setIsSubmittingForm(false)
        updateErrorState('ktpFile', 'pre_onboarding_file_upload_failed')
        return
      }

      ktpFileURL = attachmentPath

      setUploadedFiles((previousFiles) => ({
        ...previousFiles,
        ktp: previousFiles.ktp
          ? {
              ...previousFiles.ktp,
              rawFile: null,
              serverPath: attachmentPath,
            }
          : previousFiles.ktp,
      }))
    }

    if (uploadedFiles.kk?.rawFile) {
      setUploadingFields((previousFields) => ({
        ...previousFields,
        kk: true,
      }))

      const uploadResponse = await uploadMyPreOnBoardFile({
        file: uploadedFiles.kk.rawFile,
        userToken,
        candidateJobOfferID: currentJobOffer.candidateJobOfferID,
        documentType: 'kk',
      })

      setUploadingFields((previousFields) => ({
        ...previousFields,
        kk: false,
      }))

      const attachmentPath =
        uploadResponse?.attachmentPath || uploadResponse?.AttachmentPath || ''

      if (typeof uploadResponse === 'string' || !attachmentPath) {
        setIsSubmittingForm(false)
        updateErrorState('kkFile', 'pre_onboarding_file_upload_failed')
        return
      }

      kkFileURL = attachmentPath

      setUploadedFiles((previousFiles) => ({
        ...previousFiles,
        kk: previousFiles.kk
          ? {
              ...previousFiles.kk,
              rawFile: null,
              serverPath: attachmentPath,
            }
          : previousFiles.kk,
      }))
    }

    if (uploadedFiles.npwp?.rawFile) {
      setUploadingFields((previousFields) => ({
        ...previousFields,
        npwp: true,
      }))

      const uploadResponse = await uploadMyPreOnBoardFile({
        file: uploadedFiles.npwp.rawFile,
        userToken,
        candidateJobOfferID: currentJobOffer.candidateJobOfferID,
        documentType: 'npwp',
      })

      setUploadingFields((previousFields) => ({
        ...previousFields,
        npwp: false,
      }))

      const attachmentPath =
        uploadResponse?.attachmentPath || uploadResponse?.AttachmentPath || ''

      if (typeof uploadResponse === 'string' || !attachmentPath) {
        setIsSubmittingForm(false)
        updateErrorState('npwpFile', 'pre_onboarding_file_upload_failed')
        return
      }

      npwpFileURL = attachmentPath

      setUploadedFiles((previousFiles) => ({
        ...previousFiles,
        npwp: previousFiles.npwp
          ? {
              ...previousFiles.npwp,
              rawFile: null,
              serverPath: attachmentPath,
            }
          : previousFiles.npwp,
      }))
    }

    setUploadingFields({})

    const npwpDigits = getNpwpDigits(formValues.npwpNumber)

    const response = await submitMyPreOnBoarding({
      userToken,
      reqBody: {
        candidateJobOfferID: currentJobOffer.candidateJobOfferID,
        ktpNumber: formValues.ktpNumber,
        ktpFileURL,
        kkNumber: formValues.kkNumber,
        kkFileURL,
        npwpNumber: npwpDigits ? formValues.npwpNumber : null,
        npwpFileURL,
        bankName: formValues.bankName || 'BCA',
        bankAccountNumber: formValues.bankAccountNumber,
        bankAccountHolderName: formValues.bankAccountHolderName,
        emergencyContactName: formValues.emergencyContactName,
        emergencyContactPhone: formValues.emergencyContactPhone,
        emergencyContactRelationship: formValues.emergencyContactRelationship,
        isDeclarationAccepted: hasDeclared,
      },
    })

    setIsSubmittingForm(false)

    if (typeof response === 'string') {
      Swal.fire({
        title: 'Error!',
        text: response,
        icon: 'error',
        timer: 2600,
        showConfirmButton: false,
        customClass: { popup: 'my-custom-popup' },
        heightAuto: false,
        returnFocus: false,
      })
      return
    }

    setHasSubmittedPreOnboarding(true)

    Swal.fire({
      title: Lang(lang, 'pre_onboarding_submitted_title'),
      text: response?.message || Lang(lang, 'pre_onboarding_submit_success'),
      icon: 'success',
      timer: 2200,
      showConfirmButton: false,
      customClass: { popup: 'my-custom-popup' },
      heightAuto: false,
      returnFocus: false,
    })

    Swal.fire({
      title: Lang(lang, 'pre_onboarding_submitted_title'),
      text: response?.message || Lang(lang, 'pre_onboarding_submit_success'),
      icon: 'success',
      timer: 2200,
      showConfirmButton: false,
      customClass: { popup: 'my-custom-popup' },
      heightAuto: false,
      returnFocus: false,
    })

    if (typeof onRefresh === 'function') {
      await onRefresh()
    }
  }

  return (
    <div className='rounded-[12px] border border-[#E5E7EB] bg-white p-4 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-5 md:p-[25px]'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6'>
        <div className='min-w-0'>
          <h3 className='break-words text-[20px] font-medium leading-[28px] text-[#0F1729]'>
            {application?.title || '-'}
          </h3>

          <div className='mt-3 flex flex-wrap items-center'>
            <img
              src='/asset/icon/company.svg'
              alt='company'
              className='h-4 w-4'
            />
            <p className='ml-2 break-words text-[16px] font-normal leading-5 text-[#4B5563] uppercase'>
              {companyLabel}
            </p>
          </div>
        </div>

        <span
          className={
            application?.badgeClassName ||
            'inline-flex w-fit rounded-full border border-[#BFDBFE] bg-[#DBEAFE] px-[12px] py-[5px] text-[12px] font-medium leading-4 text-[#1D4ED8]'
          }
        >
          {application?.badgeLabel || Lang(lang, 'job_offer')}
        </span>
      </div>

      {isSubmitted ? (
        <ApplicationNotice
          icon={<span className='text-[12px] font-bold leading-none'>!</span>}
          tone='brand'
          className='mt-8'
        >
          <div className='flex flex-col gap-1'>
            <p>{Lang(lang, 'pre_onboarding_submitted_desc')}</p>
          </div>
        </ApplicationNotice>
      ) : isExpired ? (
        <ApplicationNotice
          icon={<span className='text-[12px] font-bold leading-none'>!</span>}
          tone='warning'
          className='mt-8'
        >
          <div className='flex flex-col gap-1'>
            <p className='font-semibold'>
              {Lang(lang, 'pre_onboarding_expired_title')}
            </p>
            <p>{Lang(lang, 'pre_onboarding_expired_desc')}</p>
          </div>
        </ApplicationNotice>
      ) : (
        <ApplicationNotice
          variant='countdown'
          className='mt-8'
          countdownLabel={Lang(lang, 'document_fill_expires_in')}
          countdownTargetDate={countdownTargetValue}
          countdownInitialValue={
            countdownSourceValue ||
            (countdownTargetValue ? null : DEFAULT_EXPIRY_COUNTDOWN)
          }
          countdownFallback={countdownSourceValue || DEFAULT_EXPIRY_COUNTDOWN}
          countdownExpiryPrefix={Lang(lang, 'expires')}
          countdownExpiryText={expiryDateLabel}
        />
      )}

      <div className='mt-8'>
        <ApplicationMetaSummary
          dateApplied={application?.dateApplied}
          location={locationLabel}
          lang={lang}
        />
      </div>

      <div className='mt-8'>
        <ApplicationProgressTimeline
          items={progressSteps}
          lang={lang}
          showBottomBorder={!isSubmitted}
        />
      </div>

      {!isSubmitted ? (
        <>
          <div className='mt-6 rounded-[20px] border border-[#E5E7EB] bg-white p-5 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-6'>
            <div className='flex flex-col gap-5'>
              <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
                <h4 className='text-[20px] font-bold leading-[28px] text-[#0F1729]'>
                  {Lang(lang, 'pre_onboarding_checklist')}
                </h4>

                <StatusBadge
                  label={`${requiredCompletedCount}/${requiredSections.length} ${Lang(
                    lang,
                    'required',
                  )}`}
                />
              </div>

              <div className='h-[8px] w-full rounded-full bg-[#F3F4F6]'>
                <div
                  className='h-full rounded-full bg-[#E8172C] transition-all'
                  style={{ width: progressWidth }}
                />
              </div>

              <p className='text-[12px] leading-[16px] text-[#6B7280]'>
                {Lang(lang, 'pre_onboarding_subtitle')}
              </p>
            </div>
          </div>
          <div className='mt-5 space-y-5'>
            <ChecklistSectionCard
              icon={<FileText className='h-5 w-5' strokeWidth={1.8} />}
              title={Lang(lang, 'ktp')}
              badgeLabel={Lang(lang, 'required')}
              description={Lang(lang, 'ktp_upload_desc')}
            >
              <TextField
                lang={lang}
                label={Lang(lang, 'ktp_number')}
                placeholder={Lang(lang, 'input_ktp')}
                value={formValues.ktpNumber}
                onChange={handleInputChange('ktpNumber')}
                onBlur={handleFieldBlur('ktpNumber')}
                error={getVisibleError('ktpNumber')}
                inputMode='numeric'
                maxLength={16}
                disabled={isFormLocked}
              />

              <UploadArea
                lang={lang}
                prompt={Lang(lang, 'upload_prompt_click_or_drag')}
                file={uploadedFiles.ktp}
                error={getVisibleError('ktpFile')}
                isUploading={uploadingFields.ktp === true}
                disabled={isFormLocked || isSubmittingForm}
                onFileChange={handleFileChange('ktp', 'ktpFile', {
                  required: true,
                })}
              />
            </ChecklistSectionCard>

            <ChecklistSectionCard
              icon={<FileText className='h-5 w-5' strokeWidth={1.8} />}
              title={Lang(lang, 'kk')}
              badgeLabel={Lang(lang, 'required')}
              description={Lang(lang, 'kk_upload_desc')}
            >
              <TextField
                lang={lang}
                label={Lang(lang, 'kk_number')}
                placeholder={Lang(lang, 'input_kk')}
                value={formValues.kkNumber}
                onChange={handleInputChange('kkNumber')}
                onBlur={handleFieldBlur('kkNumber')}
                error={getVisibleError('kkNumber')}
                inputMode='numeric'
                maxLength={16}
                disabled={isFormLocked}
              />

              <UploadArea
                lang={lang}
                prompt={Lang(lang, 'upload_prompt_click_or_drag')}
                file={uploadedFiles.kk}
                error={getVisibleError('kkFile')}
                isUploading={uploadingFields.kk === true}
                disabled={isFormLocked || isSubmittingForm}
                onFileChange={handleFileChange('kk', 'kkFile', {
                  required: true,
                })}
              />
            </ChecklistSectionCard>

            <ChecklistSectionCard
              icon={<FileText className='h-5 w-5' strokeWidth={1.8} />}
              title={Lang(lang, 'npwp')}
              badgeLabel={Lang(lang, 'optional')}
              badgeTone='optional'
              description={Lang(lang, 'npwp_upload_desc')}
            >
              <TextField
                lang={lang}
                label={Lang(lang, 'npwp_number')}
                placeholder={Lang(lang, 'input_npwp')}
                value={formValues.npwpNumber}
                onChange={handleInputChange('npwpNumber')}
                onBlur={handleFieldBlur('npwpNumber')}
                error={getVisibleError('npwpNumber')}
                inputMode='numeric'
                maxLength={20}
                disabled={isFormLocked}
              />

              <UploadArea
                lang={lang}
                prompt={Lang(lang, 'upload_prompt_click_or_drag')}
                file={uploadedFiles.npwp}
                error={getVisibleError('npwpFile')}
                isUploading={uploadingFields.npwp === true}
                disabled={isFormLocked || isSubmittingForm}
                onFileChange={handleFileChange('npwp', 'npwpFile')}
              />
            </ChecklistSectionCard>

            <ChecklistSectionCard
              icon={<Landmark className='h-5 w-5' strokeWidth={1.8} />}
              title={Lang(lang, 'bank_account_details')}
              badgeLabel={Lang(lang, 'required')}
              description={Lang(lang, 'bank_account_upload_desc')}
            >
              <div className='grid gap-4 md:grid-cols-3'>
                <TextField
                  lang={lang}
                  label={Lang(lang, 'bank_name')}
                  placeholder={Lang(lang, 'input_bank_name')}
                  value='BCA'
                  onChange={handleInputChange('bankName')}
                  onBlur={handleFieldBlur('bankName')}
                  error={getVisibleError('bankName')}
                  readOnly={true}
                  disabled={true}
                />

                <TextField
                  lang={lang}
                  label={Lang(lang, 'account_number')}
                  placeholder={Lang(lang, 'input_account_number')}
                  value={formValues.bankAccountNumber}
                  onChange={handleInputChange('bankAccountNumber')}
                  onBlur={handleFieldBlur('bankAccountNumber')}
                  error={getVisibleError('bankAccountNumber')}
                  inputMode='numeric'
                  maxLength={20}
                  disabled={isFormLocked}
                />

                <TextField
                  lang={lang}
                  label={Lang(lang, 'account_holder_name')}
                  placeholder={Lang(lang, 'input_account_holder_name')}
                  value={formValues.bankAccountHolderName}
                  onChange={handleInputChange('bankAccountHolderName')}
                  onBlur={handleFieldBlur('bankAccountHolderName')}
                  error={getVisibleError('bankAccountHolderName')}
                  disabled={isFormLocked}
                />
              </div>
            </ChecklistSectionCard>

            <ChecklistSectionCard
              icon={<Phone className='h-5 w-5' strokeWidth={1.8} />}
              title={Lang(lang, 'emergency_contact')}
              badgeLabel={Lang(lang, 'required')}
              description={Lang(lang, 'emergency_contact_desc')}
            >
              <div className='grid gap-4 md:grid-cols-3'>
                <TextField
                  lang={lang}
                  label={Lang(lang, 'full_name')}
                  placeholder={Lang(lang, 'emergency_contact_name_placeholder')}
                  value={formValues.emergencyContactName}
                  onChange={handleInputChange('emergencyContactName')}
                  onBlur={handleFieldBlur('emergencyContactName')}
                  error={getVisibleError('emergencyContactName')}
                  disabled={isFormLocked}
                />

                <TextField
                  lang={lang}
                  label={Lang(lang, 'phone_number')}
                  placeholder={Lang(
                    lang,
                    'emergency_contact_phone_placeholder',
                  )}
                  value={formValues.emergencyContactPhone}
                  onChange={handleInputChange('emergencyContactPhone')}
                  onBlur={handleFieldBlur('emergencyContactPhone')}
                  error={getVisibleError('emergencyContactPhone')}
                  inputMode='numeric'
                  maxLength={16}
                  disabled={isFormLocked}
                />

                <TextField
                  lang={lang}
                  label={Lang(lang, 'relationship')}
                  placeholder={Lang(
                    lang,
                    'emergency_contact_relationship_placeholder',
                  )}
                  value={formValues.emergencyContactRelationship}
                  onChange={handleInputChange('emergencyContactRelationship')}
                  onBlur={handleFieldBlur('emergencyContactRelationship')}
                  error={getVisibleError('emergencyContactRelationship')}
                  disabled={isFormLocked}
                />
              </div>
            </ChecklistSectionCard>

            <div className='rounded-[20px] border border-[#E5E7EB] bg-white p-5 shadow-[0px_1px_2px_rgba(0,0,0,0.05)] sm:p-6'>
              <label className='flex cursor-pointer items-start gap-4'>
                <button
                  type='button'
                  onClick={handleDeclarationToggle}
                  disabled={isFormLocked}
                  className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border transition ${
                    hasDeclared
                      ? 'border-[#EA2427] bg-[#EA2427] text-white'
                      : 'border-[#D1D5DB] bg-white text-transparent'
                  } disabled:cursor-not-allowed disabled:opacity-60`}
                  aria-pressed={hasDeclared}
                >
                  <BadgeCheck className='h-[13px] w-[13px]' strokeWidth={2.5} />
                </button>

                <div className='min-w-0 text-[12px] leading-5 text-[#4B5563]'>
                  <p className='text-[13px] font-normal leading-5 text-[#4B5563]'>
                    {Lang(lang, 'pre_onboarding_declaration_title')}
                  </p>

                  <div className='mt-2 space-y-1'>
                    {declarationStatements.map((statement, index) => (
                      <p key={`${index + 1}-${statement}`}>
                        {index + 1}. {statement}
                      </p>
                    ))}
                  </div>
                </div>
              </label>

              {getVisibleError('declaration') ? (
                <p className='mt-3 text-xs leading-4 text-[#EA2427]'>
                  {Lang(lang, getVisibleError('declaration'))}
                </p>
              ) : null}
            </div>

            <div className='flex justify-end'>
              <button
                type='button'
                onClick={handleSubmit}
                disabled={isPreOnboardingSubmitDisabled}
                className='w-full rounded-full bg-[#EA2427] px-6 py-3 text-sm font-semibold leading-5 text-white transition hover:bg-[#D91F22] disabled:cursor-not-allowed disabled:bg-[#FCA5A5] sm:w-auto sm:min-w-[148px]'
              >
                {isSubmittingForm
                  ? Lang(lang, 'loading')
                  : isSubmitted
                    ? Lang(lang, 'submitted')
                    : Lang(lang, 'submit')}
              </button>
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}

export default PreOnBoardingCard
